import type { OfflineLedgerEntry } from '@/lib/offline/offline-queue'
import type { PersistedClient } from '@tanstack/query-persist-client-core'
import {
  isOwnedOfflineEnvelope,
  shouldDehydrateOfflineMutation,
} from '@/lib/offline/offline-queue'

export interface CacheGeneration {
  ownerId: string
  generation: string | null
}

export interface CacheOwner {
  userId: string
  login: string
}

export const CACHE_PREFIX = 'scrambl-query-cache-v2-'

export function cacheScopeKey(
  owner: CacheOwner,
  organizationId: string,
  relationshipId: string | null,
  externalAccess = false,
) {
  return JSON.stringify([
    owner.userId,
    owner.login,
    organizationId,
    relationshipId,
    externalAccess,
  ])
}

export function cacheOwnerPrefix(owner: CacheOwner) {
  return `${CACHE_PREFIX}${JSON.stringify([owner.userId, owner.login]).slice(0, -1)},`
}

export function cacheBusterFor(userId: string, login: string) {
  return `owned-v4:${JSON.stringify([userId, login])}`
}

/** Older read caches have no login proof. Only immutable, owned paused work migrates. */
export function adoptLegacyOfflineSnapshot(
  legacy: PersistedClient | undefined,
  owner: CacheOwner,
  organizationId: string,
  entries: readonly OfflineLedgerEntry[],
): PersistedClient | undefined {
  if (
    !legacy ||
    legacy.buster !== `owned-v3:${owner.userId}` ||
    !Array.isArray(legacy.clientState?.mutations)
  )
    return undefined
  const mutations = legacy.clientState.mutations.filter(
    mutation =>
      mutation &&
      mutation.state &&
      shouldDehydrateOfflineMutation({
        state: mutation.state,
        options: { mutationKey: mutation.mutationKey },
      }) &&
      isOwnedOfflineEnvelope(mutation.state.variables) &&
      mutation.state.variables.ownerId === owner.userId &&
      mutation.state.variables.organizationId === organizationId &&
      entries.some(
        entry =>
          entry.status === 'queued' &&
          entry.attempts === 0 &&
          JSON.stringify(entry.envelope) ===
            JSON.stringify(mutation.state.variables),
      ),
  )
  if (!mutations.length) return undefined
  return {
    timestamp: legacy.timestamp,
    buster: cacheBusterFor(owner.userId, owner.login),
    clientState: { queries: [], mutations },
  }
}

export function cacheUserPrefixes(userId: string) {
  const owner = JSON.stringify([userId]).slice(0, -1) + ','
  return [CACHE_PREFIX + owner, 'scrambl-query-cache-' + owner]
}
export function cacheKeyOwner(key: string): string | null {
  const prefix = key.startsWith(CACHE_PREFIX)
    ? CACHE_PREFIX
    : 'scrambl-query-cache-'
  if (!key.startsWith(prefix)) return null
  const end = key.lastIndexOf(']-')
  if (end < prefix.length) return null
  try {
    const scope: unknown = JSON.parse(key.slice(prefix.length, end + 1))
    if (
      !Array.isArray(scope) ||
      scope.length !== (prefix === CACHE_PREFIX ? 5 : 3)
    )
      return null
    return typeof scope[0] === 'string' && scope[0] ? scope[0] : null
  } catch {
    return null
  }
}
